import { useEffect, useRef, useState, type JSX } from 'react'
import { signInHref, type Session } from './session.js'

const GoogleMark = (): JSX.Element => (
  <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M45.1 24.5c0-1.6-.1-3.2-.4-4.7H24v8.9h11.8c-.5 2.7-2 5-4.4 6.6v5.5h7.1c4.2-3.8 6.6-9.5 6.6-16.3z"
    />
    <path
      fill="#34A853"
      d="M24 46c6 0 11-2 14.5-5.2l-7.1-5.5c-2 1.3-4.5 2.1-7.4 2.1-5.7 0-10.6-3.9-12.3-9.1H4.3v5.7C7.8 41.1 15.3 46 24 46z"
    />
    <path fill="#FBBC05" d="M11.7 28.3c-.4-1.3-.7-2.7-.7-4.3s.3-3 .7-4.3v-5.7H4.3A22 22 0 0 0 2 24c0 3.6.9 6.9 2.3 9.9l7.4-5.6z" />
    <path
      fill="#EA4335"
      d="M24 10.7c3.2 0 6.1 1.1 8.4 3.3l6.3-6.3C34.9 4.1 30 2 24 2 15.3 2 7.8 6.9 4.3 14.1l7.4 5.7c1.7-5.2 6.6-9.1 12.3-9.1z"
    />
  </svg>
)

const initial = (name: string): string => (name.trim()[0] ?? '?').toUpperCase()

export interface AccountMenuProps {
  readonly session: Session
  readonly onSettings: () => void
  readonly onGames: () => void
}

/**
 * Sign-in, top right, where it never stands between a visitor and a game.
 *
 * While the session is still loading this renders nothing at all rather than a
 * skeleton: a button that appears and then changes into a different button is
 * worse than one that arrives a moment late.
 */
export const AccountMenu = ({ session, onSettings, onGames }: AccountMenuProps): JSX.Element => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (event: PointerEvent): void => {
      if (ref.current !== null && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('pointerdown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('pointerdown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (session.loading) return <div className="h-9" />

  if (session.user === null) {
    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onSettings}
          aria-label="Settings"
          className="rounded-full p-2 text-ink-dim hover:bg-panel hover:text-ink"
        >
          <GearIcon />
        </button>
        {session.canSignIn && (
          <a
            href={signInHref()}
            className="flex items-center gap-2 rounded-full bg-panel px-3.5 py-2 text-sm font-semibold hover:bg-panel-raised"
          >
            <GoogleMark />
            Sign in
          </a>
        )}
      </div>
    )
  }

  const { user } = session

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full bg-panel py-1 pr-3 pl-1 hover:bg-panel-raised"
      >
        <Avatar user={user} />
        <span className="max-w-24 truncate text-sm font-semibold">{user.name}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="card-in absolute right-0 z-50 mt-2 w-48 overflow-hidden rounded-panel bg-panel-raised py-1 shadow-2xl"
        >
          <MenuItem
            label="Your games"
            onClick={() => {
              setOpen(false)
              onGames()
            }}
          />
          <MenuItem
            label="Settings"
            onClick={() => {
              setOpen(false)
              onSettings()
            }}
          />
          <div className="my-1 h-px bg-panel-line" />
          {/* A link, not a fetch: signing out is a navigation that must clear
              the cookie even if JavaScript has fallen over. */}
          <a
            href="/api/auth/logout"
            role="menuitem"
            className="block px-4 py-2.5 text-sm text-ink-dim hover:bg-panel hover:text-ink"
          >
            Sign out
          </a>
        </div>
      )}
    </div>
  )
}

export const Avatar = ({
  user,
  size = 'h-7 w-7',
}: {
  readonly user: { readonly name: string; readonly avatar: string | null }
  readonly size?: string
}): JSX.Element =>
  user.avatar === null ? (
    <span
      className={`${size} grid shrink-0 place-items-center rounded-full bg-accent text-sm font-bold text-black`}
    >
      {initial(user.name)}
    </span>
  ) : (
    <img
      src={user.avatar}
      alt=""
      // Google's picture host sets no CORS headers we need, but it does honour
      // Referrer-Policy; sending no referrer keeps the visit off their logs.
      referrerPolicy="no-referrer"
      className={`${size} shrink-0 rounded-full object-cover`}
    />
  )

const MenuItem = ({
  label,
  onClick,
}: {
  readonly label: string
  readonly onClick: () => void
}): JSX.Element => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    className="block w-full px-4 py-2.5 text-left text-sm hover:bg-panel"
  >
    {label}
  </button>
)

export const GearIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  </svg>
)
